/**
 * KITDayForm.jsx
 * 
 * Modal form for adding KIT (Keeping in Touch) days.
 * Styled with MUI components.
 */

import React, { useState } from 'react';
import api from '../axiosConfig';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    TextField,
    IconButton,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    Close as CloseIcon,
    WorkOff as KITIcon
} from '@mui/icons-material';

const KITDayForm = ({ open, onClose, maternityId, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        kit_date: '',
        hours_worked: '',
        description: ''
    });

    const handleSubmit = async () => {
        if (!formData.kit_date || !formData.hours_worked) {
            setError('Please fill in all required fields');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await api.post(`/maternity/${maternityId}/kit-day`, {
                kit_date: formData.kit_date,
                hours_worked: parseFloat(formData.hours_worked),
                description: formData.description
            });
            
            // Reset form
            setFormData({ kit_date: '', hours_worked: '', description: '' });
            onSuccess();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add KIT day');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({ kit_date: '', hours_worked: '', description: '' });
        setError(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <KITIcon sx={{ color: '#8b5cf6' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Record KIT Day
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                            <strong>Keeping in Touch (KIT) Days:</strong> Employees can work up to 10 KIT days 
                            during maternity leave without affecting their leave or pay. These are voluntary 
                            for both employer and employee.
                        </Typography>
                    </Alert>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            type="date"
                            label="Date"
                            value={formData.kit_date}
                            onChange={(e) => setFormData({ ...formData, kit_date: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            required
                        />

                        <TextField
                            type="number"
                            label="Hours Worked"
                            value={formData.hours_worked}
                            onChange={(e) => setFormData({ ...formData, hours_worked: e.target.value })}
                            inputProps={{ min: 0.5, max: 12, step: 0.5 }}
                            helperText="Any amount of work on a day counts as one KIT day"
                            fullWidth
                            required
                        />

                        <TextField
                            label="Description / Purpose"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="e.g., Team meeting, training session, handover..."
                            multiline
                            rows={2}
                            fullWidth
                        />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, pt: 0 }}>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !formData.kit_date || !formData.hours_worked}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{ 
                        background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                        '&:hover': { opacity: 0.9 }
                    }}
                >
                    {loading ? 'Saving...' : 'Save KIT Day'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default KITDayForm;